import { useState, useEffect } from "react";
import { Menu, X, Heart } from "lucide-react";
import { BRAND } from "@/constants/brand";

const NAV_LINKS = [
  { label: "Mission", id: "mission" },
  { label: "Programs", id: "programs" },
  { label: "About", id: "about" },
  { label: "Get Involved", id: "get-involved" },
  { label: "Contact", id: "contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0b0d10]/90 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <button
            data-testid="nav-logo"
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-3 group"
          >
            <img
              src={BRAND.logo}
              alt="DADication logo"
              className="h-16 w-16 object-contain"
            />
            <span className="font-display text-2xl md:text-3xl uppercase tracking-wide text-white">
              DADication
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-[#e2562a] transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:w-0 after:bg-[#e2562a] after:transition-all hover:after:w-full"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:block">
            <button
              data-testid="nav-donate-btn"
              onClick={() => scrollTo("get-involved")}
              className="inline-flex items-center gap-2 bg-[#e2562a] hover:bg-[#c4451d] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 shadow-[0_8px_24px_-8px_rgba(226,86,42,0.6)]"
            >
              <Heart className="h-4 w-4" strokeWidth={2.5} />
              Donate
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden backdrop-blur-xl bg-[#0b0d10]/97 border-b border-white/10 px-6 py-6"
        >
          <nav className="flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-left text-sm font-semibold uppercase tracking-widest text-white/80 hover:text-[#e2562a] transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-donate-btn"
              onClick={() => handleNav("get-involved")}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#e2562a] text-white px-6 py-3 font-bold uppercase tracking-wider"
            >
              <Heart className="h-4 w-4" strokeWidth={2.5} /> Donate
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
